import useUrlSearchState from "@/hooks/useUrlSearchState";
import { ChevronRight, Clock, Crown, Gamepad2, Lock, MapPin, Play, Search, Trophy, Users, Zap } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import SpecialGameActions from "@/components/game/SpecialGameActions";

function getDifficultySnapshotPresentation(snapshot) {
  return {
    label: snapshot?.label || "Novo",
  };
}

function CommunityQuizCard({
  commentActionSubmitting,
  deleteCommunityComment,
  isAdmin,
  loading,
  onReportComment,
  onStartQuiz,
  quiz,
  user,
}) {
  const difficultyPresentation = getDifficultySnapshotPresentation(quiz.difficulty_snapshot);

  return (
    <div className="rounded-2xl border border-white/10 bg-black/20 p-4 flex flex-col gap-3">
      <div>
        <p className="font-nunito font-bold text-lg text-white">{quiz.title}</p>
        <p className="font-outfit text-sm text-zinc-400 mt-1">
          {`${quiz.question_count} perguntas • ${quiz.category_name || quiz.curadoria || "Comunidade"}`}
        </p>
      </div>
      {quiz.description ? (
        <p className="font-outfit text-sm text-zinc-300 line-clamp-3">{quiz.description}</p>
      ) : null}
      <div className="flex flex-wrap gap-2 text-xs">
        {quiz.theme ? <span className="rounded-full border border-white/10 bg-white/5 px-2.5 py-1 text-zinc-300">{quiz.theme}</span> : null}
        {quiz.creator_display_name ? <span className="rounded-full border border-emerald-500/20 bg-emerald-500/10 px-2.5 py-1 text-emerald-300">por {quiz.creator_display_name}</span> : null}
      </div>
      <div className="grid grid-cols-2 gap-2 text-sm xl:grid-cols-3">
        <div className="rounded-xl border border-white/10 bg-zinc-950/70 p-3">
          <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-zinc-500">Jogadas</p>
          <p className="mt-1 text-sm font-semibold text-white">{quiz.plays_count || 0}</p>
        </div>
        <div className="rounded-xl border border-white/10 bg-zinc-950/70 p-3">
          <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-zinc-500">Maior Score</p>
          <p className="mt-1 text-sm font-semibold text-white">{quiz.best_score || 0} pts</p>
        </div>
        <div className="rounded-xl border border-white/10 bg-zinc-950/70 p-3">
          <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-zinc-500">Score Médio</p>
          <p className="mt-1 text-sm font-semibold text-white">{quiz.average_score ?? "—"}{quiz.average_score != null ? " pts" : ""}</p>
        </div>
        <div className="rounded-xl border border-white/10 bg-zinc-950/70 p-3">
          <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-zinc-500">Nível</p>
          <p className="mt-1 text-sm font-semibold text-white">{difficultyPresentation.label}</p>
        </div>
        <div className="rounded-xl border border-white/10 bg-zinc-950/70 p-3">
          <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-zinc-500">Nota Média</p>
          <p className="mt-1 text-sm font-semibold text-white">{quiz.feedback_snapshot?.average_rating ?? "—"}</p>
        </div>
        <div className="rounded-xl border border-white/10 bg-zinc-950/70 p-3">
          <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-zinc-500">Comentários</p>
          <p className="mt-1 text-sm font-semibold text-white">{quiz.feedback_snapshot?.comments_count || 0}</p>
        </div>
      </div>
      {quiz.feedback_snapshot?.comments_preview?.length ? (
        <div className="rounded-xl border border-white/10 bg-zinc-950/60 p-3">
          <p className="font-outfit text-[11px] uppercase tracking-[0.18em] text-zinc-500 mb-2">Comentários da comunidade</p>
          <div className="space-y-2">
            {quiz.feedback_snapshot.comments_preview.slice(0, 2).map((comment) => (
              <div key={`${quiz.id}-${comment.id}`} className="rounded-lg border border-white/10 bg-black/20 px-3 py-2">
                <div className="flex items-center justify-between gap-3 mb-1 text-xs text-zinc-500">
                  <span className="truncate">{comment.display_name}</span>
                  <div className="flex items-center gap-2">
                    <span>{comment.rating ? `${comment.rating}/5` : "Comentário"}</span>
                    {isAdmin ? (
                      <button
                        type="button"
                        onClick={() => deleteCommunityComment(comment.id)}
                        disabled={commentActionSubmitting[`delete-comment:${comment.id}`]}
                        className="text-red-300 hover:text-red-200 disabled:opacity-60"
                      >
                        Excluir
                      </button>
                    ) : user && comment.user_id !== user.id ? (
                      <button
                        type="button"
                        onClick={() => onReportComment(comment.id)}
                        disabled={commentActionSubmitting[`report-comment:${comment.id}`]}
                        className="text-zinc-400 hover:text-zinc-200 disabled:opacity-60"
                      >
                        Reportar
                      </button>
                    ) : null}
                  </div>
                </div>
                <p className="font-outfit text-sm text-zinc-300 line-clamp-2">{comment.comment}</p>
              </div>
            ))}
          </div>
        </div>
      ) : null}
      <div className="rounded-xl border border-white/10 bg-zinc-950/60 p-3">
        <p className="font-outfit text-[11px] uppercase tracking-[0.18em] text-zinc-500 mb-2">Ranking deste quiz</p>
        {quiz.leaderboard_preview?.length ? (
          <div className="space-y-2">
            {quiz.leaderboard_preview.map((entry) => (
              <div key={`${quiz.id}-${entry.user_id || entry.rank}`} className="flex items-center justify-between gap-3 text-sm">
                <div className="flex items-center gap-2 min-w-0">
                  <span className="w-6 text-zinc-500 font-bold">#{entry.rank}</span>
                  <span className="truncate text-zinc-200">{entry.display_name}</span>
                </div>
                <span className="font-bold text-[#fccf1f]">{entry.best_score} pts</span>
              </div>
            ))}
          </div>
        ) : (
          <p className="font-outfit text-sm text-zinc-400">Ainda ninguém entrou no ranking deste quiz.</p>
        )}
      </div>
      <Button
        type="button"
        onClick={() => onStartQuiz(quiz.id)}
        disabled={loading}
        className="mt-auto bg-emerald-400 text-black font-bold hover:bg-emerald-300"
      >
        <Play className="w-4 h-4 mr-2" /> Jogar quiz
      </Button>
    </div>
  );
}

export default function GamesCatalogSections({
  allThemes,
  commentActionSubmitting,
  communityQuizzes,
  deleteCommunityComment,
  eventNeedsTicket,
  eventUsesGamePass,
  gamesGridConfig,
  getGamePassCost,
  getOnlineEventPath,
  isAdmin,
  loading,
  openOnlineEventPurchase,
  premiumEvents,
  specialEvents,
  specialGamesHasMore,
  specialGamesLoading,
  specialGamesQuery,
  specialGamesTotal,
  orderedGameSections,
  presencialEvents,
  reportCommunityComment,
  resolveThemeIcon,
  sectionIndexOffset = 0,
  setShowAuth,
  setSpecialGamesQuery,
  startCommunityQuiz,
  startPremiumGame,
  startSpecialGame,
  startGame,
  themeGridClasses,
  user,
  userCanAccessOnlineEvent,
  hasAvailableGamePassForEvent,
  loadMoreSpecialGames,
}) {
  const [communitySearch, setCommunitySearch] = useUrlSearchState("comunidade", "");
  const normalizedCommunitySearch = communitySearch
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();
  const filteredCommunityQuizzes = communityQuizzes.filter((quiz) => {
    if (!normalizedCommunitySearch) return true;
    const searchableText = [
      quiz.title,
      quiz.description,
      quiz.theme,
      quiz.category_name,
      quiz.curadoria,
      quiz.creator_display_name,
    ]
      .filter(Boolean)
      .join(" ")
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .toLowerCase();
    return searchableText.includes(normalizedCommunitySearch);
  });

  return orderedGameSections.map((sectionKey, sectionIndex) => {
    const effectiveIndex = sectionIndex + sectionIndexOffset;

    if (sectionKey === "community") {
      return (
        <div
          key={sectionKey}
          className={`${effectiveIndex > 0 ? "mt-10" : ""} rounded-3xl border border-white/10 bg-zinc-900/60 backdrop-blur-md p-5 md:p-6`}
          data-testid="community-quiz-gallery"
          data-catalog-palette="community"
        >
          <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between mb-4">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-xl bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center">
                <Users className="w-5 h-5 text-emerald-300" />
              </div>
              <div>
                <h2 className="font-nunito font-bold text-2xl text-white uppercase tracking-wide">{gamesGridConfig.communitySection.title}</h2>
                <p className="font-outfit text-zinc-400 text-sm">
                  {gamesGridConfig.communitySection.subtitle}
                </p>
                {user && user.subscription_plan !== "free" && (
                  <p className="font-outfit text-sm text-zinc-300 mt-3 max-w-2xl">
                    {gamesGridConfig.communitySection.creatorDescription}
                  </p>
                )}
              </div>
            </div>
            {user && user.subscription_plan !== "free" && (
              <Link to="/quizmaker">
                <Button className="bg-[#fccf1f] text-black font-nunito font-bold uppercase hover:bg-[#e6bc1c]">
                  <Gamepad2 className="w-4 h-4 mr-2" /> Crie o seu quiz
                </Button>
              </Link>
            )}
          </div>
          {communityQuizzes.length > 0 ? (
            <>
              <div className="relative mb-4 max-w-xl">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-zinc-500" />
                <Input
                  value={communitySearch}
                  onChange={(event) => setCommunitySearch(event.target.value)}
                  placeholder="Buscar por tema, título ou categoria"
                  className="border-white/10 bg-black/25 pl-10 text-white placeholder:text-zinc-500"
                  data-testid="community-quiz-search"
                />
              </div>
              {filteredCommunityQuizzes.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                  {filteredCommunityQuizzes.map((quiz) => (
                    <CommunityQuizCard
                      key={quiz.id}
                      commentActionSubmitting={commentActionSubmitting}
                      deleteCommunityComment={deleteCommunityComment}
                      isAdmin={isAdmin}
                      loading={loading}
                      onReportComment={reportCommunityComment}
                      onStartQuiz={startCommunityQuiz}
                      quiz={quiz}
                      user={user}
                    />
                  ))}
                </div>
              ) : (
                <div className="rounded-2xl border border-dashed border-white/10 bg-black/20 p-5 text-sm text-zinc-400">
                  Nenhum quiz da comunidade foi encontrado para esta busca.
                </div>
              )}
            </>
          ) : (
            <div className="rounded-2xl border border-dashed border-white/10 bg-black/20 p-5 text-sm text-zinc-400">
              {gamesGridConfig.communitySection.emptyText}
            </div>
          )}
        </div>
      );
    }

    if (sectionKey === "online") {
      return (
        <div key={sectionKey} className={effectiveIndex > 0 ? "mt-10" : ""} data-catalog-palette="online">
          <div className="mb-6">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-xl bg-[#fccf1f]/10 border border-[#fccf1f]/20 flex items-center justify-center">
                <Gamepad2 className="w-5 h-5 text-[#fccf1f]" />
              </div>
              <div>
                <h2 className="font-nunito font-bold text-2xl text-white uppercase tracking-wide">{gamesGridConfig.onlineSection.title}</h2>
                <p className="font-outfit text-zinc-400 text-sm">
                  {gamesGridConfig.onlineSection.subtitle}
                </p>
              </div>
            </div>
          </div>

          <div className={`grid ${themeGridClasses} gap-3 md:gap-4`}>
            {allThemes.map((theme) => {
              const ThemeIcon = resolveThemeIcon(theme.name);
              const isAvailable = Boolean(theme.available);

              return (
                <div
                  key={`${theme.category_id}-${theme.name}`}
                  className={`rounded-2xl border p-3.5 md:p-4 transition-all duration-300 min-h-[132px] flex flex-col ${
                    isAvailable
                      ? "bg-zinc-900/60 border-[#fccf1f]/50 shadow-[0_0_30px_rgba(252,207,31,0.08)] hover:scale-[1.02] hover:border-[#fccf1f]/80"
                      : "bg-zinc-900/40 border-zinc-700 opacity-80"
                  }`}
                  data-testid={`theme-card-${theme.name.toLowerCase().replace(/ /g, "-")}`}
                >
                  <div className="flex items-start justify-between gap-3 mb-3">
                    <div className={`w-10 h-10 rounded-2xl flex items-center justify-center border ${
                      isAvailable ? "bg-[#fccf1f]/10 border-[#fccf1f]/20" : "bg-zinc-800 border-zinc-700"
                    }`}>
                      <ThemeIcon className={`w-5 h-5 ${isAvailable ? "text-[#fccf1f]" : "text-zinc-500"}`} />
                    </div>
                    {!isAvailable && <Lock className="w-4 h-4 text-zinc-500 shrink-0" />}
                  </div>

                  <div className="flex-1">
                    <h3 className={`font-nunito font-bold text-base leading-tight ${isAvailable ? "text-[#fccf1f]" : "text-white"}`}>
                      {theme.name}
                    </h3>
                    <p className={`font-outfit text-xs mt-1.5 ${isAvailable ? "text-zinc-400" : "text-zinc-500"}`}>
                      {theme.question_count || 10} perguntas
                    </p>
                  </div>

                  <Button
                    type="button"
                    onClick={() => {
                      if (isAvailable) {
                        startGame(theme.category_id, theme.name);
                      }
                    }}
                    disabled={loading || !isAvailable}
                    className={`mt-3 w-full font-nunito font-bold uppercase h-10 ${
                      isAvailable
                        ? "bg-[#fccf1f] text-black hover:bg-[#e6bc1c] shadow-[0_0_20px_rgba(252,207,31,0.3)]"
                        : "bg-zinc-800 text-zinc-500 hover:bg-zinc-800 cursor-not-allowed"
                    }`}
                  >
                    {isAvailable ? (
                      <>
                        <Play className="w-4 h-4 mr-2" /> Jogar
                      </>
                    ) : (
                      "Em breve"
                    )}
                  </Button>
                </div>
              );
            })}
          </div>

          {allThemes.length === 0 && (
            <div className="text-center py-20">
              <Gamepad2 className="w-16 h-16 text-zinc-700 mx-auto mb-4" />
              <p className="font-outfit text-xl text-zinc-500">{gamesGridConfig.onlineSection.emptyText}</p>
            </div>
          )}
        </div>
      );
    }

    if (sectionKey === "special") {
      return (
        <div key={sectionKey} className={`${effectiveIndex > 0 ? "mt-10" : ""}`} data-testid="special-games-section" data-catalog-palette="special">
          <div className="mb-5 flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-purple-600 to-pink-600 flex items-center justify-center shadow-[0_0_16px_rgba(168,85,247,0.3)]">
                <Zap className="w-5 h-5 text-white" />
              </div>
              <div>
                <h2 className="font-nunito font-bold text-2xl text-white uppercase tracking-wide">{gamesGridConfig.specialSection.title}</h2>
                {gamesGridConfig.specialSection.subtitle ? (
                  <p className="font-outfit text-zinc-400 text-sm">{gamesGridConfig.specialSection.subtitle}</p>
                ) : null}
              </div>
            </div>
            <div className="w-full lg:max-w-md">
              <div className="relative">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-purple-300" />
                <Input
                  value={specialGamesQuery}
                  onChange={(event) => setSpecialGamesQuery(event.target.value)}
                  placeholder="Buscar por título, tema ou palavra-chave"
                  className="border-purple-500/30 bg-zinc-950/70 pl-10 text-white placeholder:text-zinc-500 focus-visible:ring-purple-500"
                  data-testid="special-games-search"
                />
              </div>
              {specialGamesTotal > 0 ? (
                <p className="mt-2 text-right font-outfit text-xs text-zinc-500">
                  {specialGamesTotal} {specialGamesTotal === 1 ? "jogo disponível" : "jogos disponíveis"}
                </p>
              ) : null}
            </div>
          </div>
          {specialEvents.length > 0 ? (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {specialEvents.map((ev) => (
                <div
                  key={ev.id}
                  className="bg-zinc-900/60 backdrop-blur-md border border-purple-500/30 rounded-2xl p-6 flex flex-col gap-3 hover:border-purple-500/60 transition-all"
                  data-testid={`special-game-card-${ev.id}`}
                >
                  {ev.image_url && (
                    <img src={ev.image_url} alt={ev.title} className="w-full h-32 object-cover rounded-xl mb-1" />
                  )}
                  <h3 className="font-nunito font-bold text-lg text-white">{ev.title}</h3>
                  {ev.description && <p className="font-outfit text-sm text-zinc-400 whitespace-pre-line break-words">{ev.description}</p>}
                  <div className="flex flex-wrap gap-2">
                    {ev.quiz_themes?.map((theme) => (
                      <span key={theme} className="text-xs font-outfit bg-purple-500/20 text-purple-300 px-2 py-0.5 rounded-full">{theme}</span>
                    ))}
                  </div>
                  <p className="font-outfit text-sm text-zinc-300 dark:text-zinc-400">
                    {ev.quiz_question_count || 10} perguntas • {ev.quiz_time_per_question || 30}s por pergunta
                  </p>
                  {ev.prize_description && (
                    <p className="font-outfit text-xs text-[#fccf1f] flex items-center gap-1">
                      <Trophy className="w-3 h-3" /> {ev.prize_description}
                    </p>
                  )}
                  <SpecialGameActions event={ev} onPlay={startSpecialGame} disabled={loading} />
                </div>
                ))}
              </div>
              {specialGamesHasMore ? (
                <div className="mt-5 flex justify-center">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={loadMoreSpecialGames}
                    disabled={specialGamesLoading}
                    className="border-purple-400/40 bg-purple-500/5 font-nunito font-bold text-purple-100 hover:bg-purple-500/15 hover:text-white"
                    data-testid="load-more-special-games"
                  >
                    {specialGamesLoading ? "Carregando..." : "Ver mais Jogos Especiais"}
                  </Button>
                </div>
              ) : null}
            </>
          ) : (
            <div className="bg-zinc-900/40 backdrop-blur-md border border-purple-500/20 rounded-2xl p-8 text-center" data-testid="no-special-games">
              <p className="font-outfit text-zinc-400 text-sm leading-relaxed max-w-md mx-auto">
                {specialGamesQuery ? "Nenhum Jogo Especial foi encontrado para esta busca." : gamesGridConfig.specialSection.emptyText}
              </p>
            </div>
          )}
        </div>
      );
    }

    if (sectionKey === "premium") {
      return (
        <div key={sectionKey} className={`${effectiveIndex > 0 ? "mt-10" : ""}`} data-testid="premium-games-section" data-catalog-palette="premium">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-sky-500 to-indigo-600 flex items-center justify-center shadow-[0_0_16px_rgba(59,130,246,0.3)]">
              <Crown className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="font-nunito font-bold text-2xl text-white uppercase tracking-wide">{gamesGridConfig.premiumSection?.title || "Jogos Premium"}</h2>
              <p className="font-outfit text-zinc-400 text-sm">{gamesGridConfig.premiumSection?.subtitle || "Eventos online com experiências ao vivo e acesso exclusivo."}</p>
            </div>
          </div>
          {premiumEvents.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {premiumEvents.map((ev) => (
                (() => {
                  const needsGamePass = eventUsesGamePass?.(ev);
                  const needsUnlock = eventNeedsTicket?.(ev) && !userCanAccessOnlineEvent?.(ev);
                  const canUsePass = needsGamePass && needsUnlock && hasAvailableGamePassForEvent?.(ev);
                  const isPaused = needsUnlock && ev.ticket_sales_paused;
                  const passCost = getGamePassCost?.(ev) || 1;
                  return (
                <div key={ev.id} className="bg-zinc-900/60 backdrop-blur-md border border-sky-500/25 rounded-2xl p-6 flex flex-col gap-3 hover:border-sky-400/60 transition-all" data-testid={`premium-game-card-${ev.id}`}>
                  {ev.image_url ? <img src={ev.image_url} alt={ev.title} className="w-full h-32 object-cover rounded-xl mb-1" /> : null}
                  <h3 className="font-nunito font-bold text-lg text-white">{ev.title}</h3>
                  {ev.quiz_themes?.length ? <div className="flex flex-wrap gap-2">{ev.quiz_themes.map((theme) => <span key={theme} className="text-xs font-outfit bg-sky-500/15 text-sky-200 px-2 py-0.5 rounded-full">{theme}</span>)}</div> : null}
                  <p className="font-outfit text-sm text-zinc-300">{ev.quiz_question_count || 10} perguntas • {ev.quiz_time_per_question || 30}s por pergunta</p>
                  {needsUnlock && (!canUsePass || isPaused) ? (
                    <div className="mt-1 flex flex-col gap-2">
                      <Button type="button" onClick={() => openOnlineEventPurchase?.(ev)} disabled={isPaused} className="w-full bg-[#fccf1f] text-black font-nunito font-bold uppercase hover:bg-[#e6bc1c]" data-testid={`buy-premium-btn-${ev.id}`}>{isPaused ? "Indisponível" : needsGamePass ? "Comprar passes" : "Comprar"}</Button>
                      {ev.is_streaming && ev.streaming_mode === "embedded_room" ? <Link to={getOnlineEventPath?.(ev) || `/sala-evento/${ev.id}`} className="block"><Button variant="outline" className="w-full border-sky-500/40 text-sky-100 hover:bg-sky-500/10 font-nunito font-bold uppercase">Entrar como Expectador</Button></Link> : null}
                    </div>
                  ) : (
                    <Button type="button" onClick={() => startPremiumGame?.(ev.id)} className="w-full bg-gradient-to-r from-sky-500 to-indigo-600 text-white font-nunito font-bold uppercase hover:from-sky-400 hover:to-indigo-500 mt-1" data-testid={`play-premium-btn-${ev.id}`}><Play className="w-4 h-4 mr-2" /> {canUsePass ? `Usar ${passCost} ${passCost === 1 ? "passe" : "passes"}` : ev.is_streaming && ev.streaming_mode === "embedded_room" ? "Entrar" : "Jogar"}</Button>
                  )}
                </div>
                  );
                })()
              ))}
            </div>
          ) : (
            <div className="bg-zinc-900/40 backdrop-blur-md border border-sky-500/20 rounded-2xl p-8 text-center"><p className="font-outfit text-zinc-400 text-sm leading-relaxed max-w-md mx-auto">{gamesGridConfig.premiumSection?.emptyText || "Nenhum jogo premium ativo no momento."}</p></div>
          )}
        </div>
      );
    }

    return (
      <div key={sectionKey} className={`${effectiveIndex > 0 ? "mt-10" : ""}`} data-testid="presencial-events-section" data-catalog-palette="presencial">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#fccf1f] to-amber-600 flex items-center justify-center shadow-[0_0_16px_rgba(252,207,31,0.2)]">
            <MapPin className="w-5 h-5 text-black" />
          </div>
          <div>
            <h2 className="font-nunito font-bold text-2xl text-white uppercase tracking-wide">{gamesGridConfig.presencialSection.title}</h2>
            <p className="font-outfit text-zinc-400 text-sm">{gamesGridConfig.presencialSection.subtitle}</p>
          </div>
        </div>
        {presencialEvents.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {presencialEvents.map((ev) => (
              <Link key={ev.id} to={ev.slug ? `/eventos/${ev.slug}` : "/eventos"} className="block" data-testid={`presencial-event-${ev.id}`}>
                <div className="bg-zinc-900/60 backdrop-blur-md border border-[#fccf1f]/20 rounded-2xl p-6 flex flex-col gap-2 hover:border-[#fccf1f]/50 transition-all h-full">
                  {ev.image_url && (
                    <img src={ev.image_url} alt={ev.title} className="w-full h-32 object-cover rounded-xl mb-1" />
                  )}
                  <h3 className="font-nunito font-bold text-lg text-white">{ev.title}</h3>
                  {ev.date && (
                    <p className="font-outfit text-sm text-[#fccf1f] flex items-center gap-1.5">
                      <Clock className="w-3.5 h-3.5" />
                      {`${ev.date.slice(8, 10)}-${ev.date.slice(5, 7)}-${ev.date.slice(0, 4)}`}
                      {ev.time && ` • ${ev.time}`}
                    </p>
                  )}
                  {ev.venue && (
                    <p className="font-outfit text-xs text-zinc-500 flex items-center gap-1">
                      <MapPin className="w-3 h-3" /> {ev.venue.name}
                    </p>
                  )}
                </div>
              </Link>
            ))}
          </div>
        ) : (
          <p className="font-outfit text-zinc-500 text-sm">{gamesGridConfig.presencialSection.emptyText}</p>
        )}
        <div className="mt-4 text-center">
          <Link to="/eventos">
            <Button variant="outline" className="border-[#fccf1f]/30 text-[#fccf1f] hover:bg-[#fccf1f]/10 font-nunito font-bold uppercase" data-testid="view-all-events-btn">
              <ChevronRight className="w-4 h-4 mr-1" /> {gamesGridConfig.presencialSection.ctaLabel}
            </Button>
          </Link>
        </div>
      </div>
    );
  });
}
